import { useCallback, useEffect, useState } from 'react';
import {
  growthplannerApi,
  type ConflictRecord,
  type ConsolidatedRecommendation,
  type ExecutionProgress,
  type ExecutiveBrief,
  type ForecastRecord,
  type PlanAction,
  type PlannerDashboard,
  type PlanPerformance,
  type ScenarioPlan,
  type StrategicPlan,
} from '../../api/growthplanner';

type Tab = 'overview' | 'plans' | 'scenarios' | 'conflicts' | 'recommendations' | 'execution' | 'briefing' | 'forecasts';

function statusClass(s: string) {
  if (s === 'active' || s === 'in_progress') return 'text-emerald-700 bg-emerald-50';
  if (s === 'pending_approval' || s === 'pending') return 'text-amber-700 bg-amber-50';
  if (s === 'blocked') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function horizonLabel(h: string) {
  return h.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function GrowthPlannerPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<PlannerDashboard | null>(null);
  const [plans, setPlans] = useState<StrategicPlan[]>([]);
  const [actions, setActions] = useState<PlanAction[]>([]);
  const [conflicts, setConflicts] = useState<ConflictRecord[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioPlan[]>([]);
  const [forecasts, setForecasts] = useState<ForecastRecord[]>([]);
  const [recommendations, setRecommendations] = useState<ConsolidatedRecommendation[]>([]);
  const [execution, setExecution] = useState<ExecutionProgress[]>([]);
  const [brief, setBrief] = useState<ExecutiveBrief | null>(null);
  const [performance, setPerformance] = useState<PlanPerformance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, pl, act, conf, sc, fc, recs, exec, br, perf] = await Promise.all([
        growthplannerApi.dashboard(),
        growthplannerApi.listPlans(),
        growthplannerApi.listActions(),
        growthplannerApi.listConflicts(),
        growthplannerApi.listScenarios(),
        growthplannerApi.listForecasts(),
        growthplannerApi.listRecommendations(),
        growthplannerApi.listExecution(),
        growthplannerApi.executiveBrief(),
        growthplannerApi.listPerformance(),
      ]);
      setDashboard(dash);
      setPlans(pl);
      setActions(act);
      setConflicts(conf);
      setScenarios(sc);
      setForecasts(fc);
      setRecommendations(recs);
      setExecution(exec);
      setBrief(br);
      setPerformance(perf);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load growth planner data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const consolidate = async () => {
    setBusy(true);
    try { await growthplannerApi.consolidateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Dashboard' },
    { id: 'plans', label: 'Strategic Plans' },
    { id: 'scenarios', label: 'Scenario Simulator' },
    { id: 'conflicts', label: 'Conflict Resolution' },
    { id: 'recommendations', label: 'Strategy Workspace' },
    { id: 'execution', label: 'Execution Tracker' },
    { id: 'briefing', label: 'Business Review' },
    { id: 'forecasts', label: 'Forecast Dashboard' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Autonomous Growth Planner</h1>
          <p className="text-sm text-slate-500">Strategic decision engine &amp; executive business planning — BCP-00008</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Active Plans', value: dashboard.active_plans.toString() },
              { label: 'Pending Approvals', value: dashboard.pending_approvals.toString() },
              { label: 'Consolidated Recs', value: dashboard.consolidated_recommendations.toString() },
              { label: 'Resolved Conflicts', value: dashboard.resolved_conflicts.toString() },
              { label: 'Active Scenarios', value: dashboard.active_scenarios.toString() },
              { label: 'Forecast Confidence', value: `${dashboard.avg_forecast_confidence.toFixed(0)}%` },
              { label: 'Plan Completion', value: `${dashboard.avg_plan_completion_pct.toFixed(0)}%` },
              { label: 'Execution Velocity', value: `${dashboard.execution_velocity.toFixed(1)}/wk` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          {performance.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Plan Performance</h3>
              <p className="text-sm text-slate-600 mt-1">
                Revenue achieved {performance[0].revenue_achieved_pct.toFixed(0)}% · Profit {performance[0].profit_achieved_pct.toFixed(0)}% ·
                Forecast accuracy {performance[0].forecast_accuracy_pct.toFixed(0)}% · Impact score {performance[0].business_impact_score.toFixed(0)}
              </p>
            </div>
          )}
        </div>
      )}

      {tab === 'plans' && (
        <div className="space-y-4">
          {plans.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs capitalize ${statusClass(p.status ?? 'draft')}`}>{p.status?.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-slate-500">{horizonLabel(p.horizon)}</span>
                  </div>
                  <h3 className="font-medium text-slate-900 mt-1">{p.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    +{p.expected_revenue_impact_pct}% revenue · +{p.expected_profit_impact_pct}% profit · {p.action_count} actions · {p.conflict_count} conflicts resolved
                  </p>
                  {p.source_modules && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.source_modules.map((m) => (
                        <span key={m} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{m}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-semibold">{p.completion_pct.toFixed(0)}%</p>
                  <p className="text-xs text-slate-400">complete</p>
                  {p.requires_approval && (
                    <button type="button" onClick={() => growthplannerApi.approvePlan(p.id).then(refresh)}
                      className="mt-2 rounded bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-200">
                      Approve
                    </button>
                  )}
                </div>
              </div>
              {actions.filter((a) => a.plan_id === p.id).length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                  {actions.filter((a) => a.plan_id === p.id).slice(0, 3).map((a) => (
                    <div key={a.id} className="flex justify-between text-sm">
                      <span className="text-slate-700">#{a.priority_rank} {a.title}</span>
                      <span className={`rounded px-2 py-0.5 text-xs capitalize ${statusClass(a.status ?? 'pending')}`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {scenarios.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{s.name}</h3>
              <p className="text-xs text-slate-500 capitalize mt-1">{s.type.replace(/_/g, ' ')}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-emerald-600 font-medium">+{s.revenue_impact_pct}%</p><p className="text-xs text-slate-400">Revenue</p></div>
                <div><p className="text-sky-600 font-medium">+{s.profit_impact_pct}%</p><p className="text-xs text-slate-400">Profit</p></div>
                <div><p className="font-medium">${s.ad_budget_estimate.toLocaleString()}</p><p className="text-xs text-slate-400">Ad Budget</p></div>
                <div><p className="font-medium">${s.inventory_investment.toLocaleString()}</p><p className="text-xs text-slate-400">Inventory</p></div>
              </div>
              <p className="mt-2 text-xs text-slate-500">Risk {(s.risk_score * 100).toFixed(0)}% · {s.action_count} actions · {s.ai_credit_estimate.toLocaleString()} AI credits</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'conflicts' && (
        <div className="space-y-4">
          {conflicts.map((c) => (
            <div key={c.id} className="rounded-xl border border-amber-200 bg-amber-50/30 p-4 shadow-sm">
              <span className="text-xs font-medium uppercase text-amber-700">{c.conflict_type.replace(/_/g, ' ')}</span>
              <div className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-slate-400">Action A</p><p>{c.action_a}</p></div>
                <div><p className="text-xs text-slate-400">Action B</p><p>{c.action_b}</p></div>
              </div>
              <div className="mt-3 rounded-lg bg-white p-3 border border-emerald-200">
                <p className="text-xs font-medium text-emerald-700">Resolution</p>
                <p className="text-sm text-slate-800 mt-1">{c.resolution}</p>
                {c.rationale && <p className="text-xs text-slate-500 mt-1">{c.rationale}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={consolidate} disabled={busy}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              Consolidate from All Modules
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className="text-xs font-medium text-sky-600">#{r.priority_rank} · {r.source_module}</span>
                  <h3 className="font-medium text-slate-900 mt-1">{r.title}</h3>
                  {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
                  <p className="text-xs text-slate-500 mt-1">
                    +{r.revenue_impact_pct}% revenue · +{r.profit_impact_pct}% profit · Risk {(r.risk_score * 100).toFixed(0)}%
                  </p>
                  {r.conflict_with && r.conflict_with.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">Conflicts: {r.conflict_with.join(', ')}</p>
                  )}
                </div>
                <span className={`rounded px-2 py-0.5 text-xs ${r.included_in_plan ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}`}>
                  {r.included_in_plan ? 'In Plan' : 'Pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'execution' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Workflow</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {execution.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2 font-medium">{e.plan_id}</td>
                  <td className="px-4 py-2 text-slate-600">{e.workflow_ref ?? e.release_ref ?? '—'}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs capitalize ${statusClass(e.status)}`}>{e.status.replace(/_/g, ' ')}</span></td>
                  <td className="px-4 py-2">{e.progress_pct.toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'briefing' && brief && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-medium text-slate-900 capitalize">{brief.period} Executive Brief</h3>
            <p className="text-sm text-slate-600 mt-2">{brief.revenue_outlook}</p>
            <p className="text-sm text-amber-700 mt-1">{brief.risk_summary}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4">
              <h4 className="text-sm font-medium text-emerald-800">Top Opportunities</h4>
              <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc list-inside">
                {brief.top_opportunities?.map((o) => <li key={o}>{o}</li>)}
              </ul>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50/30 p-4">
              <h4 className="text-sm font-medium text-red-800">Top Threats</h4>
              <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc list-inside">
                {brief.top_threats?.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          </div>
          {brief.plan_highlights && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h4 className="text-sm font-medium text-slate-800">Plan Highlights</h4>
              <ul className="mt-2 space-y-1 text-sm text-slate-600 list-disc list-inside">
                {brief.plan_highlights.map((h) => <li key={h}>{h}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{horizonLabel(f.horizon)}</h3>
              <p className="text-2xl font-semibold text-slate-900 mt-2">${f.revenue_forecast.toLocaleString()}</p>
              <p className="text-sm text-slate-500">Revenue forecast</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><p className="font-medium">${f.profit_forecast.toLocaleString()}</p><p className="text-xs text-slate-400">Profit</p></div>
                <div><p className="font-medium">{f.conversion_forecast_pct}%</p><p className="text-xs text-slate-400">Conversion</p></div>
                <div><p className="font-medium">{f.acos_forecast_pct}%</p><p className="text-xs text-slate-400">ACOS</p></div>
                <div><p className="font-medium">{(f.confidence_score * 100).toFixed(0)}%</p><p className="text-xs text-slate-400">Confidence</p></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
